import type { ExtractionCitation } from '@ctw/shared/api/ai/use-extraction-jobs';
import { Button } from '@ctw/shared/components/button';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { notify } from '@ctw/shared/components/toast';
import {
  collectExportFields,
  computeExportSummary,
  isDeliverable,
} from '@ctw/shared/content/fact-extraction/compute-export-summary';
import type { MappedFactGroup } from '@ctw/shared/content/fact-extraction/map-extraction-job';
import {
  type AtlasExportProgress,
  runAtlasExport,
} from '@ctw/shared/content/fact-extraction/run-atlas-export';
import { useCitationSources } from '@ctw/shared/content/fact-extraction/use-citation-sources';
import { useExportDocuments } from '@ctw/shared/content/fact-extraction/use-export-documents';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { useModal } from '@ctw/shared/context/modal-context';
import { usePatientContext } from '@ctw/shared/context/patient-provider';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { downloadBlob } from '@ctw/shared/utils/documents';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import {
  faCheck,
  faDownload,
  faFileLines,
  faFileSpreadsheet,
  faMinus,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import pluralize from 'pluralize';
import { useEffect, useRef, useState } from 'react';

export interface PatientFactExtractionExportModalBodyProps {
  patientName: string;
  groups: Array<MappedFactGroup>;
  excludedIds: Array<string>;
  citations: Array<ExtractionCitation>;
  jobId: string;
  configName?: string;
  onClose: () => void;
}

export const PatientFactExtractionExportModalBody = ({
  patientName,
  groups,
  excludedIds,
  citations,
  jobId,
  configName,
  onClose,
}: PatientFactExtractionExportModalBodyProps) => {
  const { patient } = usePatientContext();
  const { requestContext, fetchFromFqs } = useCTW();
  const telemetry = useTelemetry();

  // Called here (not in the page) so counts update live if resolution finishes
  // while the modal is open. Both queries are cached and shared.
  const { sourceByCitationId, isLoading: isLoadingSources } = useCitationSources(citations);
  const {
    exportDocumentsByBinaryId,
    isLoading: isLoadingDocuments,
    isError: isResolutionError,
  } = useExportDocuments(sourceByCitationId);
  const isLoading = isLoadingSources || isLoadingDocuments;

  const summary = computeExportSummary({
    groups,
    excludedIds,
    sourceByCitationId,
    exportDocumentsByBinaryId,
  });
  const totalFieldsCount = summary.includedFieldsCount + summary.excludedFieldsCount;
  const undeliverableSources = summary.includedSources.filter((source) => !isDeliverable(source));

  const [progress, setProgress] = useState<AtlasExportProgress | undefined>(undefined);
  const [isExporting, setIsExporting] = useState(false);
  const abortRef = useRef<AbortController | undefined>(undefined);

  // The export must always read the CURRENT auth token/FQS fetcher — a
  // worst-case run takes ~40-90s and tokens refresh via React state, so a
  // click-time capture would go stale. The ref is updated every render.
  const ctwRef = useRef({ authToken: requestContext.authToken, fetchFromFqs });
  ctwRef.current = { authToken: requestContext.authToken, fetchFromFqs };

  // Closing the modal mid-export cancels in-flight work.
  useEffect(() => () => abortRef.current?.abort(), []);

  // A resolution FAILURE blocks download: exporting in that state would
  // silently produce a zip with zero source documents.
  const canDownload =
    !(isLoading || isExporting || isResolutionError) && summary.includedFieldsCount > 0;

  const handleDownload = async () => {
    const startedAt = performance.now();
    const abortController = new AbortController();
    abortRef.current = abortController;
    setIsExporting(true);
    setProgress(undefined);
    try {
      const fields = collectExportFields({ groups, excludedIds });
      // Attach the citation refs each document backs — the manifest's join
      // key to the CSV's citations column. Only citations from INCLUDED
      // fields participate: refs belonging to explicitly excluded fields
      // must not leak into the export.
      const includedCitationIds = new Set(fields.flatMap((field) => field.citationIds));
      const citationById = new Map(citations.map((citation) => [citation.id, citation]));
      const citationRefsByBinaryId = new Map<string, Array<string>>();
      for (const [citationId, source] of sourceByCitationId) {
        if (!includedCitationIds.has(citationId)) {
          continue;
        }
        const citation = citationById.get(citationId);
        if (source.kind === 'document' && citation) {
          const refs = citationRefsByBinaryId.get(source.binaryId) ?? [];
          refs.push(`${citation.resourceType}/${citation.resourceId}`);
          citationRefsByBinaryId.set(source.binaryId, refs);
        }
      }
      const exportDocuments = summary.includedSources.map((source) => ({
        ...source,
        citationRefs: citationRefsByBinaryId.get(source.binaryId) ?? [],
      }));
      const result = await runAtlasExport({
        deps: {
          env: requestContext.env,
          patientId: patient.id,
          getAuthToken: () => ctwRef.current.authToken,
          getFetchFromFqs: () => ctwRef.current.fetchFromFqs,
        },
        fields,
        citations,
        patientName,
        upid: patient.UPID ?? '',
        jobId,
        configName,
        exportDocuments,
        includedFieldsCount: summary.includedFieldsCount,
        excludedFieldsCount: summary.excludedFieldsCount,
        documentedIncludedCount: summary.documentedIncludedCount,
        signal: abortController.signal,
        onProgress: setProgress,
      });
      downloadBlob(result.blob, result.fileName);
      telemetry.trackInteraction('atlas_export_download', {
        includedFieldsCount: summary.includedFieldsCount,
        documentCount: result.includedCount,
        failedDocumentCount: result.failedCount,
        unsupportedDocumentCount: result.unsupportedCount,
        durationMs: Math.round(performance.now() - startedAt),
      });
      notify({
        type: 'success',
        title: `Export ready - ${pluralize('field', summary.includedFieldsCount, true)}, ${pluralize('source document', result.includedCount, true)}`,
      });
      onClose();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }
      telemetry.trackInteraction('atlas_export_failed', {});
      notify({
        type: 'error',
        title: 'Unable to build export',
        body: 'An error occurred while assembling the export package. Please try again.',
      });
    } finally {
      setIsExporting(false);
      setProgress(undefined);
    }
  };

  // Groups contributing nothing to the export sink to the bottom of the list.
  const orderedGroupSummaries = [
    ...summary.groupSummaries.filter((group) => group.includedCount > 0),
    ...summary.groupSummaries.filter((group) => group.includedCount === 0),
  ];

  return (
    <div className={tw`flex w-[480px] max-w-full flex-col`}>
      <div className={tw`border-divider-main border-b px-6 py-5`}>
        <div className={tw`font-semibold text-content-main text-lg`}>Export Package</div>
        <div className={tw`mt-0.5 text-content-subtle text-sm`}>{patientName}</div>
      </div>
      {isLoading ? (
        // The whole body waits on source resolution — rendering counts that
        // fill in afterward reads as pop-in. min-height keeps the modal from
        // jumping when the real content lands.
        <div className={tw`flex min-h-[280px] items-center justify-center px-6 py-5`}>
          <LoadingSpinner message="Resolving sources..." />
        </div>
      ) : (
        <div className={tw`space-y-5 px-6 py-5`}>
          <div className={tw`space-y-4`}>
            <div className={tw`flex items-start gap-3`}>
              <FontAwesomeIcon
                icon={faFileSpreadsheet}
                className={tw`mt-0.5 h-4 w-4 text-primary-main`}
              />
              <div>
                <div className={tw`font-semibold text-content-main text-sm`}>Field Data</div>
                <div className={tw`text-content-subtle text-xs`}>
                  CSV · {summary.includedFieldsCount} of{' '}
                  {pluralize('field', totalFieldsCount, true)} included ·{' '}
                  {summary.documentedIncludedCount} documented
                </div>
              </div>
            </div>
            <div className={tw`flex items-start gap-3`}>
              <FontAwesomeIcon
                icon={faFileLines}
                className={tw`mt-0.5 h-4 w-4 text-caution-main`}
              />
              <div>
                <div className={tw`font-semibold text-content-main text-sm`}>Sources</div>
                {isResolutionError ? (
                  <div className={tw`text-error-text text-xs`}>
                    Source documents could not be resolved. Close this dialog and try again.
                  </div>
                ) : (
                  <>
                    <div className={tw`text-content-subtle text-xs`}>
                      {summary.deliverableSourcesCount} of{' '}
                      {pluralize('source', summary.totalSourcesCount, true)} included
                    </div>
                    {undeliverableSources.map((source) => (
                      <div key={source.binaryId} className={tw`mt-1 text-content-subtle text-xs`}>
                        {source.title} — {source.reason}
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>
          <div className={tw`space-y-1.5`}>
            {orderedGroupSummaries.map((group) => {
              const included = group.includedCount > 0;
              return (
                <div key={group.label} className={tw`flex items-center gap-2 text-sm`}>
                  <FontAwesomeIcon
                    icon={included ? faCheck : faMinus}
                    className={twx('h-3.5 w-3.5 shrink-0', {
                      'text-success-main': included,
                      'text-content-icon': !included,
                    })}
                  />
                  <span
                    className={twx('min-w-0 break-words', {
                      'text-content-main': included,
                      'text-content-subtle': !included,
                    })}
                  >
                    {group.label}
                  </span>
                  <span className={tw`whitespace-nowrap text-content-subtle`}>
                    · {pluralize('field', group.includedCount, true)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div
        className={tw`flex items-center justify-between gap-3 border-divider-main border-t px-6 py-4`}
      >
        <div className={tw`text-content-subtle text-sm`}>
          {isExporting && progress
            ? `Preparing documents… ${progress.completedDocuments} of ${progress.totalDocuments}`
            : isExporting
              ? 'Preparing export…'
              : isLoading
                ? ''
                : `1 spreadsheet + ${pluralize('source', summary.deliverableSourcesCount, true)}`}
        </div>
        <div className={tw`flex gap-3`}>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={() => void handleDownload()}
            disabled={!canDownload}
          >
            <div className={tw`flex items-center space-x-2 whitespace-nowrap`}>
              <FontAwesomeIcon icon={faDownload} className={tw`h-3.5 w-3.5`} />
              <span>{isExporting ? 'Exporting…' : 'Download ZIP'}</span>
            </div>
          </Button>
        </div>
      </div>
    </div>
  );
};

export const usePatientFactExtractionExportModal = () => {
  const telemetry = useTelemetry();
  const { openModal, closeModal } = useModal({
    boundaryName: 'AtlasExportModal',
    layout: {
      mode: 'fit',
      overflowBehavior: 'visible',
      fullbleed: true,
      showCloseButton: true,
    },
  });

  return (props: Omit<PatientFactExtractionExportModalBodyProps, 'onClose'>) => {
    openModal({
      telemetry,
      action: {
        label: 'Export Package',
        title: props.patientName,
        content: {
          body: <PatientFactExtractionExportModalBody {...props} onClose={closeModal} />,
        },
      },
    });
  };
};
